/**
 * V2 Save a place — /save (2026-08-03), the Android share-target's
 * landing page and a universal quick-save URL.
 *
 * Android's share sheet GETs here with the shared payload in ?url=,
 * ?text=, or ?title= (Google Maps puts the link inside text); opened
 * bare, the page is a paste box. Either way the link resolves through
 * the paste-a-link machinery to ONE confirm card, then the standard
 * save controls (Want to go + the Add-to-list dialog) take over —
 * saveControls/handleSaveClick are reused whole.
 *
 * The location row is only created at the first save action, never at
 * view time — a shared link someone abandons must not orphan a row
 * (the 2026-07-27 deferred-creation rule).
 */
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { createLocationFromPlace, mapsLinkCandidates } from '../log/places.js';
import { saveControls, handleSaveClick } from './save-button.js';
import { loadSaved, saveWantToGo } from './lists.js';

/** First http(s) URL inside any of the share payload fields. */
export function sharedLink(query) {
  for (const key of ['url', 'text', 'title']) {
    const match = (query.get(key) || '').match(/https?:\/\/\S+/);
    if (match) return match[0];
  }
  return null;
}

export function mount(el, ctx) {
  const { store, router } = ctx;
  let destroyed = false;
  let candidates = null; // resolved picker rows, best first
  let picked = null; // the candidate being saved
  let location = null; // created row { id, display_name } — only after a save action
  let resolveNote = ''; // on-page status/error text
  let busy = false;

  const initialLink = sharedLink(new URLSearchParams(window.location.search));

  function render() {
    const signedIn = !!store.get().user;
    const best = picked || candidates?.[0] || null;
    const alternates = best && candidates ? candidates.filter((c) => c !== best).slice(0, 3) : [];
    el.innerHTML = `
      <section class="v2-save" aria-label="Save a place">
        <h2 class="v2-page-title">Save a place</h2>
        ${best ? '' : `
        <label class="v2-field">Paste a Google Maps link
          <input type="search" data-save-input placeholder="https://maps.app.goo.gl/…"
            autocomplete="off" enterkeyhint="go" value="${escapeHtml(initialLink || '')}">
        </label>`}
        <div class="log-hint" data-save-note aria-live="polite">${escapeHtml(resolveNote)}</div>
        ${best ? `
        <div class="list-link-match">
          <div class="log-place-option list-link-best">
            <strong>${escapeHtml(best.mainText)}</strong>
            ${best.secondaryText ? `<span>${escapeHtml(best.secondaryText)}</span>` : ''}
            <span class="list-add-badge">From your link</span>
          </div>
          ${signedIn ? (location
            ? `${saveControls(store, location.id)}
               <button type="button" class="v2-btn-more" data-save-open>Open the place</button>`
            : `<button type="button" class="v2-btn-leather list-link-add" data-save-first
                 ${busy ? 'disabled' : ''}>Save to Want to go</button>`)
            : '<p class="v2-empty">Sign in (top right) to save it — the place will wait.</p>'}
          ${!location && alternates.length ? `
          <button type="button" class="list-link-alt-toggle" data-save-alternates>
            Not the right place? Show other matches</button>
          <div class="log-place-results" data-save-alt-rows hidden>
            ${alternates.map((p) => `
            <button type="button" class="log-place-option" data-save-pick="${escapeHtml(p.placeId)}">
              <strong>${escapeHtml(p.mainText)}</strong>
              ${p.secondaryText ? `<span>${escapeHtml(p.secondaryText)}</span>` : ''}
            </button>`).join('')}
          </div>` : ''}
          ${!location ? `
          <button type="button" class="list-link-alt-toggle" data-save-restart>Save a different link</button>` : ''}
        </div>` : ''}
        <p class="log-hint">Tip: in Google Maps, Share → Yoink Adventures lands here.
          A link pasted from anywhere works too.</p>
      </section>`;
  }

  async function resolve(link) {
    resolveNote = 'Reading your Maps link…';
    candidates = null;
    picked = null;
    location = null;
    render();
    let result;
    try { result = await mapsLinkCandidates(link); } catch { result = null; }
    if (destroyed) return;
    if (result?.rejected) {
      resolveNote = 'That link points to a whole area, not a specific place — try linking the place itself.';
    } else if (!result?.candidates?.length) {
      resolveNote = 'Couldn’t read that link — check it’s a Google or Apple Maps place link.';
    } else {
      resolveNote = '';
      candidates = result.candidates;
    }
    render();
  }

  /** The row is born on the first save action (never at view time). */
  async function ensureLocation() {
    if (location) return location;
    const best = picked || candidates?.[0];
    const result = await createLocationFromPlace({ placeId: best.placeId, mainText: best.mainText });
    if (result.rejected) throw new Error('That’s a whole area, not a place.');
    location = result.location;
    return location;
  }

  async function saveFirst(button) {
    if (busy) return;
    busy = true;
    button.disabled = true;
    try {
      await ensureLocation();
      await saveWantToGo(store, location.id);
      showToast(`${location.display_name} saved.`, { type: 'success' });
    } catch (err) {
      showToast(err?.message || 'Couldn’t save just now — try again.');
    } finally {
      busy = false;
    }
    if (!destroyed) render();
  }

  function onClick(event) {
    if (location && handleSaveClick(event, { store, locationId: location.id, el })) return;
    const pick = event.target.closest('[data-save-pick]');
    if (pick) {
      picked = candidates.find((c) => c.placeId === pick.dataset.savePick) || null;
      render();
      return;
    }
    if (event.target.closest('[data-save-alternates]')) {
      el.querySelector('[data-save-alt-rows]')?.removeAttribute('hidden');
      event.target.closest('[data-save-alternates]').hidden = true;
      return;
    }
    if (event.target.closest('[data-save-first]')) {
      saveFirst(event.target.closest('[data-save-first]'));
      return;
    }
    if (event.target.closest('[data-save-restart]')) {
      candidates = null;
      picked = null;
      resolveNote = '';
      render();
      el.querySelector('[data-save-input]')?.focus();
      return;
    }
    if (event.target.closest('[data-save-open]')) {
      if (location) router.navigate('location', { id: location.id });
    }
  }

  const onInput = debounce((event) => {
    if (!event.target.closest('[data-save-input]')) return;
    const value = event.target.value.trim();
    if (/^https?:\/\//i.test(value)) resolve(value);
  }, 300);

  // Auth settles late; a sign-in mid-page keeps the resolved card and
  // swaps the prompt for the save buttons. Saved-set changes (the WTG
  // toggle inside handleSaveClick) re-render the controls' state.
  let lastUserId = store.get().user?.id || null;
  let lastSaved = store.get().savedLocationIds;
  const unsubscribe = store.subscribe((s) => {
    if (destroyed) return;
    const uid = s.user?.id || null;
    if (uid !== lastUserId) {
      lastUserId = uid;
      loadSaved(store);
      render();
    } else if (s.savedLocationIds !== lastSaved) {
      lastSaved = s.savedLocationIds;
      render();
    }
  });

  el.addEventListener('click', onClick);
  el.addEventListener('input', onInput);
  loadSaved(store);
  render();
  if (initialLink) resolve(initialLink);
  else el.querySelector('[data-save-input]')?.focus();

  return () => {
    destroyed = true;
    unsubscribe();
    el.removeEventListener('click', onClick);
    el.removeEventListener('input', onInput);
  };
}
